import React, { useState, useMemo } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Breadcrumbs } from '../layout/Breadcrumbs';
import { LocationsTable } from './LocationsTable';
import { LocationForm } from './LocationForm';
import { FilterPanel, type LocationFilters } from './FilterPanel';
import { Pagination, type PaginationData } from './Pagination';
import { useLocations } from '../../hooks/locations/useLocations';
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations';
import { useQueryClient } from '@tanstack/react-query';
import type { Location } from '../../types/location.types';

export const LocationsPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'map'>('table');
  const queryClient = useQueryClient();

  // Загружаем список организаций для фильтра
  const { data: organizations } = useOrganizationsForSelect();

  // Фильтры для UI
  const [uiFilters, setUiFilters] = useState<LocationFilters>({
    search: '',
    organization_id: undefined,
  });

  // Пагинация
  const [pagination, setPagination] = useState<{ page: number; limit: number }>({
    page: 1,
    limit: 10,
  });

  // Преобразование UI фильтров в API фильтры
  // Примечание: Backend ожидает organizationId (camelCase)
  const apiFilters = useMemo(() => {
    const filters: Record<string, unknown> = {
      page: pagination.page,
      limit: pagination.limit,
    };

    if (uiFilters.search) {
      filters.search = uiFilters.search;
    }

    if (uiFilters.organization_id) {
      filters.organizationId = uiFilters.organization_id;
    }

    return filters;
  }, [uiFilters, pagination]);

  const { data: locationsData, isLoading } = useLocations(apiFilters);



  const handleCreateNew = () => {
    setEditingLocation(null);
    setIsFormOpen(true);
  };

  const handleRefresh = () => {
    console.log('[LocationsPage] Refreshing locations data...');
    // Принудительно очищаем и инвалидируем кэш локаций
    queryClient.removeQueries({ queryKey: ['locations'] });
    queryClient.invalidateQueries({ queryKey: ['locations'] });
    queryClient.invalidateQueries({ queryKey: ['employee-locations'] });
    console.log('[LocationsPage] Cache cleared and invalidated');
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingLocation(null);
  };

  const handleFiltersChange = (newFilters: LocationFilters) => {
    setUiFilters(newFilters);
    // Сбрасываем на первую страницу при изменении фильтров
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (limit: number) => {
    setPagination({ page: 1, limit });
  };

  // Подготовка данных для компонентов
  const paginationData: PaginationData = {
    page: locationsData?.page || 1,
    limit: locationsData?.limit || 10,
    total: locationsData?.total || 0,
    totalPages: Math.ceil((locationsData?.total || 0) / (locationsData?.limit || 10)),
  };

  return (
    <div className="flex flex-col h-full gap-4 overflow-y-auto">
      {/* Хлебные крошки */}
      <Breadcrumbs />

      {/* Заголовок и статистика */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Локации</h1>
          <p className="text-muted-foreground mt-2">
            Управление точками контроля и QR кодами
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRefresh} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Обновить
          </Button>
          <Button onClick={handleCreateNew} className="gap-2">
            <Plus className="h-4 w-4" />
            Новая локация
          </Button>
        </div>
      </div>

      {/* Панель фильтров */}
      <FilterPanel
        filters={uiFilters}
        onFiltersChange={handleFiltersChange}
        resultCount={locationsData?.total || 0}
        organizations={organizations || []}
      />

      {/* Переключатель вида (пока только таблица) */}
      <div className="flex gap-2">
        <Button
          variant={viewMode === 'table' ? 'default' : 'outline'}
          onClick={() => setViewMode('table')}
          size="sm"
        >
          Таблица
        </Button>
        <Button
          variant={viewMode === 'map' ? 'default' : 'outline'}
          onClick={() => setViewMode('map')}
          size="sm"
          disabled
        >
          Карта (скоро)
        </Button>
      </div>

      {/* Таблица или карта */}
      {viewMode === 'table' ? (
        <div className="flex flex-col flex-1 min-h-0 gap-4">
          <div className="flex-1 min-h-[500px]">
            <LocationsTable
              locations={locationsData?.data || []}
              isLoading={isLoading}
              onEdit={handleEdit}
            />
          </div>
          {locationsData && locationsData.total > 0 && (
            <Pagination
              pagination={paginationData}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          )}
        </div>
      ) : (
        <Card>
          <CardContent className="flex items-center justify-center h-96">
            <p className="text-muted-foreground">Карта будет доступна в следующей версии</p>
          </CardContent>
        </Card>
      )}

      {/* Модальное окно формы */}
      {isFormOpen && (
        <LocationForm
          location={editingLocation}
          isOpen={isFormOpen}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};